import $ from 'jquery';
import React from 'react';
import isEqual from 'react-fast-compare';

import {SegmentConsentProps} from 'analytics/CookieConsentManagerContainer';
import CookieConsentModal from 'analytics/CookieConsentModal';
import * as CookieConsentModalReduxActions from 'analytics/CookieConsentModalReduxActions';
import {
  ConsentPreferences,
  getNonSegmentDestinationIds,
  initAnalyticsFromPreferences,
  ESSENTIAL_DESTINATION_IDS,
} from 'analytics/Destinations';
import {assertAndContinue} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import ModalAnchor from 'ui-library/ModalAnchor';

export type CookieConsentManagerProps = SegmentConsentProps;

interface EnhancedProps {
  isModalOpen: boolean;
  onOpenModal: () => void;
  onCloseModal: () => void;
}

class CookieConsentManagerConnector extends DataConnector<CookieConsentManagerProps, EnhancedProps> {
  enhanceProps(): EnhancedProps {
    return {
      isModalOpen: this.reduxState().ui.cookieConsentModal.isOpen,
      onOpenModal: () => {
        this.dispatch(CookieConsentModalReduxActions.openModal());
      },
      onCloseModal: () => {
        this.dispatch(CookieConsentModalReduxActions.closeModal());
      },
    };
  }
}

class CookieConsentManager extends React.Component<CookieConsentManagerProps & EnhancedProps> {
  isAnalyticsInitialized = false;

  componentDidMount(): void {
    $('#data-collection-preferences-link').click(() => {
      this.props.onOpenModal();
    });

    if (
      this.props.isConsentRequired &&
      this.getDestinationIds().some(
        (destinationId) =>
          // Do not show the modal if the user only has essential cookie destinations
          // (otherwise the modal will show up on every page load).
          !ESSENTIAL_DESTINATION_IDS.includes(destinationId) &&
          !this.props.preferences.hasOwnProperty(destinationId)
      )
    ) {
      this.props.onOpenModal();
    } else {
      this.initAnalyticsIfNecessary(this.props.preferences);
    }
  }

  private initAnalyticsIfNecessary(preferences: ConsentPreferences): void {
    if (!this.isAnalyticsInitialized) {
      this.isAnalyticsInitialized = true;
      initAnalyticsFromPreferences(preferences);
    }
  }

  private handleSubmit = (newPreferences: ConsentPreferences): void => {
    this.props.saveConsent(newPreferences);
    this.props.onCloseModal();
    this.initAnalyticsIfNecessary(newPreferences);
  };

  private getDestinationIds(): Array<string> {
    return [...this.props.destinations.map((d) => d.id), ...getNonSegmentDestinationIds()];
  }

  render(): JSX.Element | null {
    return (
      <ModalAnchor open={this.props.isModalOpen}>
        {this.props.isModalOpen && (
          <CookieConsentModal
            onRequestSubmit={this.handleSubmit}
            // Even on cancel, CookieConsentModal should always call submit, since cancellation from
            // the user counts as implicit consent.
            onRequestClose={() => {
              assertAndContinue(false, 'Expected CookieConsentModal to never cancel.');
            }}
            destinationIds={this.getDestinationIds()}
            initialPreferences={this.props.preferences}
          />
        )}
      </ModalAnchor>
    );
  }
}

export default CookieConsentManagerConnector.connect(CookieConsentManager, {
  throttleUpdates: true,
  shouldPropagateNextState: (a, b) => !isEqual(a, b),
});
